import { Http } from '@okapi/http';
import type { HttpRequestConfig, HttpCancelToken, HttpStatic } from '@okapi/http';
import { BasketLogicEnv } from 'types/env';
import { isKZDomain } from 'utils/common';

export type ApiRequestConfig = HttpRequestConfig;
export type ApiCancelToken = HttpCancelToken;

export class Api {
  _finishInit?: () => void;

  _initialized: Promise<void>;

  http?: HttpStatic;

  constructor() {
    this._initialized = new Promise(resolve => {
      this._finishInit = resolve;
    });
  }

  private tryWaitHttpInstanceInitialized = async () => {
    if (!this.http) {
      await this._initialized;
    }
  }

  setEnvParams = (env: BasketLogicEnv, host?: string) => {
    this.http = new Http({
      baseURL: host || (isKZDomain(location.host) ? env.ORCHESTRATOR_HOST_KZ : env.ORCHESTRATOR_HOST),
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json;charset=UTF-8',
        'x-api-key': env.API_KEY,
      },
      withCredentials: true,
    });
    this._finishInit?.();
  }

  public abortController = async () => {
    await this.tryWaitHttpInstanceInitialized();
    return this.http?.abortController();
  }

  public isCancel = (e: unknown) => {
    return e && this.http?.isCancel(e as Error) || false;
  }

  post = <Response = unknown, Data extends Record<string, unknown> = Record<string, unknown>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig
  ) =>
    this._doFetch<Response>(url, {
      method: 'POST',
      data,
      ...config,
    });

  get = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = `${url}?${params}`;

    return this._doFetch<Response>(resultUrl, { method: 'GET', ...config });
  };

  private async _doFetch<Response = unknown>(url: string, init: ApiRequestConfig) {
    await this.tryWaitHttpInstanceInitialized();
    const resultUrl = /^\/\//.test(url as string) ? `https:${url}` : url;
    return this.http!
      .request<Response>(resultUrl, init)
      .then(response => (response.status === 200 ? response.data : Promise.reject(response)));
  }
}

export const api = new Api();
